<template>
  <v-navigation-drawer
    id="appDrawer"
    :value="drawerVisible"
    fixed
    dark
    app
    width="250"
    :permanent="!isMobile"
    :temporary="isMobile"
    :mobile-breakpoint="mobileBreakpoint"
    @input="onDrawerInput"
  >
    <!-- drawer header -->
    <v-toolbar dark height="60">
      <v-toolbar-title class="mt-1">
        <img :src="userOrganisationLogo" alt="Dashboard" />
      </v-toolbar-title>
    </v-toolbar>

    <vue-perfect-scrollbar
      :settings="scrollSettings"
      class="drawer-menu--scroll"
    >
      <v-list dense dark expand>
        <app-drawer-child
          v-for="(item, key) in menu"
          :key="key"
          :is-opened="isMenuOpened(item)"
          :item="item"
        />
      </v-list>
    </vue-perfect-scrollbar>

    <!-- drawer footer -->
    <template #append>
      <v-container>
        <v-row>
          <v-col md="12" class="powered-by" v-if="false">
            <div class="powered-by-text">
              {{ $t("FalconSidebarMenu.PoweredBy") }}
            </div>
            <img :src="falconEdgeFullWhiteLogo" />
          </v-col>
        </v-row>
      </v-container>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { get } from "lodash-es";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

import AppDrawerChild from "./AppDrawerChild";

import { FALCON_EDGE_FULL_BLACK, FALCON_EDGE_FULL_WHITE } from "@/src/image";
import { MOBILE_VIEWPORT } from "@/shared/constants/ui.constant";

export default {
  name: "AppDrawer",

  components: {
    VuePerfectScrollbar,
    AppDrawerChild,
  },

  props: {
    menu: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Boolean,
      default: false,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      falconEdgeFullBlackLogo: FALCON_EDGE_FULL_BLACK,
      falconEdgeFullWhiteLogo: FALCON_EDGE_FULL_WHITE,
      mobileBreakpoint: Number(MOBILE_VIEWPORT),
      scrollSettings: {
        maxScrollbarLength: 160,
      },
    };
  },

  computed: {
    drawerVisible() {
      return this.isMobile ? this.value : true;
    },
  },

  methods: {
    onDrawerInput(isOpen) {
      if (this.isMobile) {
        this.$emit("input", isOpen);
      }
    },
    generateLink(item, subItem) {
      if (subItem.href) return {};
      if (subItem.component) {
        return {
          name: subItem.component,
          query: subItem.query,
        };
      }

      return {
        name: this.localePath(`${item.group}-${subItem.name}`),
        query: subItem.query,
      };
    },

    isMenuOpened(item) {
      const path =
        get(this, "$route.path", null) !== null
          ? get(this, "$route.path", "")
          : "";
      const mainPath = path.split("/")[1];

      // define menu is opened or not
      return item.name === mainPath || item.group === mainPath;
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep#appDrawer {
  overflow: hidden;

  @media (max-width: 700px) {
    &.v-navigation-drawer--temporary {
      z-index: 100 !important;
    }
  }

  .v-navigation-drawer__content {
    overflow-y: hidden;
  }

  .drawer-menu--scroll {
    height: calc(100vh - 131px); // 70px is height of logo wrapper + 61px footer
    overflow: auto;

    a {
      &:hover {
        text-decoration: none;
      }
    }

    .v-list {
      padding: 24px 0;

      &.v-list--dense {
        .v-list-item {
          min-height: 32px;
          color: #fff !important;
          caret-color: #fff !important;

          &::before {
            background-color: #292929;
          }

          // active
          &.text--darken-3 {
            background-color: var(--bg-secondary) !important;
            border-color: var(--bg-secondary) !important;

            &.theme--dark {
              &::before {
                opacity: 0;
              }
            }

            .v-list-item__action {
              i {
                color: var(--color) !important;
                caret-color: var(--color) !important;
              }
            }
            // bolding text
            .v-list-item__content {
              .v-list-item__title {
                color: var(--color) !important;
                font-weight: bold;
              }
            }

            .v-list-item__icon {
              .mdi-chevron-down {
                color: var(--color) !important;
              }
            }
          }

          // icon
          .v-list-item__action {
            margin: 0 16px 0 0;
            min-width: 16px;

            i {
              font-size: 16px;
              color: #fff !important;
              caret-color: #fff !important;
            }
          }

          .v-list-item__content {
            padding: 0;

            .v-list-item__title {
              font-size: 14px;
              color: #fff !important;
              caret-color: #fff !important;
            }
          }

          &:hover {
            &::before {
              opacity: 0;
            }

            background-color: var(--bg-secondary) !important;
            border-color: var(--bg-secondary) !important;

            .v-list-item__action {
              i {
                color: var(--color) !important;
                caret-color: var(--color) !important;
              }
            }

            .v-list-item__title {
              color: var(--color) !important;
              caret-color: var(--color) !important;
            }

            .v-list-item__icon {
              .mdi-chevron-down {
                color: var(--color) !important;
              }
            }
          }
        }

        // nav with group
        .v-list-group {
          // parent menu
          .v-list-item.v-list-group__header {
            > .v-list-item {
              padding-left: 0;
              width: calc(100% - 20px);

              + .v-list-item__icon {
                min-width: 0;
                width: 20px;
                margin-left: 0;
                text-align: right;
              }
            }
          }

          // arrow icon
          .v-list-item__icon {
            .mdi-chevron-down {
              color: #fff !important;

              &::before {
                content: "\F035D";
              }
            }
          }

          // child menu
          .v-list-group__items {
            // title of child menu
            .v-list-group__header {
              > .v-list-item {
                padding-left: 32px;
              }
            }

            // child menu
            > .v-list-item {
              padding-left: 48px;

              &.v-list-item--active:not(.text--darken-3) {
                background-color: var(--bg-secondary) !important;
                border-color: var(--bg-secondary) !important;

                &.theme--dark {
                  &::before {
                    opacity: 0;
                  }
                }

                .v-list-item__content {
                  .v-list-item__title {
                    font-weight: bold;
                  }
                }
              }
            }

            // child of child menu
            .nested-sidebar {
              .v-list-group__items {
                .v-list-item {
                  background-color: #fff !important;
                  border-color: #fff !important;

                  // active
                  &.text--darken-3 {
                    background-color: var(--bg-secondary) !important;
                    border-color: var(--bg-secondary) !important;

                    // icon
                    .v-list-item__action {
                      i {
                        color: #fff !important;
                        caret-color: #fff !important;
                      }
                    }

                    // text
                    .v-list-item__content {
                      .v-list-item__title {
                        color: #fff !important;
                        caret-color: #fff !important;
                      }
                    }
                  }

                  &:hover {
                    background-color: #fff !important;
                    border-color: #fff !important;

                    // icon
                    .v-list-item__action {
                      i {
                        color: var(--bg-secondary) !important;
                        caret-color: var(--bg-secondary) !important;
                      }
                    }

                    // text
                    .v-list-item__content {
                      .v-list-item__title {
                        color: var(--bg-secondary) !important;
                        caret-color: var(--bg-secondary) !important;
                      }
                    }

                    // hover & active
                    &.text--darken-3 {
                      background-color: var(--bg-secondary) !important;
                      border-color: var(--bg-secondary) !important;

                      // icon
                      .v-list-item__action {
                        i {
                          color: #fff !important;
                          caret-color: #fff !important;
                        }
                      }

                      // text
                      .v-list-item__content {
                        .v-list-item__title {
                          color: #fff !important;
                          caret-color: #fff !important;
                        }
                      }
                    }
                  }

                  // icon
                  .v-list-item__action {
                    i {
                      color: var(--bg-secondary) !important;
                      caret-color: var(--bg-secondary) !important;
                    }
                  }

                  // text
                  .v-list-item__content {
                    .v-list-item__title {
                      color: var(--bg-secondary) !important;
                      caret-color: var(--bg-secondary) !important;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
}

::v-deep.v-list__group__items {
  a.v-list__tile {
    padding-left: 25px;
  }
}

::v-deep.v-toolbar {
  box-shadow: none;

  &.theme--dark.v-sheet {
    background-color: #fff;
  }

  .v-toolbar__content {
    .v-toolbar__title {
      margin-top: 0 !important;
      margin: 0 auto;

      img {
        max-height: 50px;
        max-width: 160px;
      }
    }
  }
}

::v-deep.theme--dark.v-navigation-drawer {
  background-color: var(--bg);
}

.powered-by {
  display: flex;
  justify-content: center;

  .powered-by-text {
    font-size: 10px;
    padding-right: 4px;
    color: #fff;
  }

  img {
    height: 16px;
  }
}
</style>
